import { FC, useState } from "react";
import cx from "classnames";
import {
  useCategoriesQuery,
  SortValues,
  useCoursesQuery,
  ProgressValues,
  useCourseProgressesQuery,
  usePaginatedItemsQuery,
  Category,
} from "@/fetch/academy";
import {
  GlobeIcon,
  HospitalityIcon,
  ChildCareIcon,
  SupportIcon,
  DeepDiveIcon,
  QuickLessonIcon,
  CupIcon,
  LifestyleIcon,
} from "@/components/Icon";
import Tag from "@/components/Tag";
import { Skeleton } from "@/components/Loader";
import { useFilterStatus } from "@/hooks";
import CategoryCoursesModal from "./CategoryCoursesModal";
import AcademyBannerCard from "./AcademyBannerCard";
import CategoryTitle from "./CategoryTitle";
import MobileFilterSection from "./MobileFilterSection";
import MobileCourseCard from "./MobileCourseCard";
import MobileLanguageCourseCard from "./MobileLanguageCourseCard";
import styles from "./MobileAcademyMainPage.module.scss";
import { INVISIBLE_CATEGORIES, CATEGORY_IDS } from "./Constants";

const visibleCategories = [1, 2, 3, 4];

const MobileAcademyMainPage: FC = () => {
  const { isFilterOpen, setIsFilterOpen } = useFilterStatus();
  const [isFilterSectionOpen, setIsFilterSectionOpen] =
    useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(
    undefined
  );
  const [isBannerOpen, setIsBannerOpen] = useState<boolean>(true);
  const { data: categoriesData, isLoading: isLoadingCategories } =
    useCategoriesQuery();
  const categories = categoriesData?.response || [];

  const { data: languageCoursesData, isLoading: isLanguageCoursesLoading } =
    useCoursesQuery({
      pageNum: 1,
      pageSize: 4,
      categoryIds: CATEGORY_IDS.Language,
      sort: SortValues.added,
    });

  const { data: shortCoursesData, isLoading: isShortCoursesLoading } =
    useCoursesQuery({
      pageNum: 1,
      pageSize: 2,
      categoryIds: CATEGORY_IDS.Short,
      sort: SortValues.added,
    });

  const { data: skillCoursesData, isLoading: isSkillCoursesLoading } =
    useCoursesQuery({
      pageNum: 1,
      pageSize: 2,
      categoryIds: CATEGORY_IDS.Win,
      sort: SortValues.added,
    });

  const { data: deepCoursesData, isLoading: isDeepCoursesLoading } =
    useCoursesQuery({
      pageNum: 1,
      pageSize: 2,
      categoryIds: CATEGORY_IDS.Deep,
      sort: SortValues.added,
    });

  const languageCourses = languageCoursesData?.response.results || [];
  const shortCourses = shortCoursesData?.response.results || [];
  const skillCourses = skillCoursesData?.response.results || [];
  const deepCourses = deepCoursesData?.response.results || [];

  const getCategoryIcon = (category: Category) => {
    if (category.name == "Language") return <GlobeIcon />;
    if (category.name == "Hospitality") return <HospitalityIcon />;
    if (category.name == "Working with Children") return <ChildCareIcon />;
    if (category.name == "Lifestyle")
      return <LifestyleIcon variant="outlined" />;
    if (category.name == "Quick Lessons ( <1 hour)")
      return <QuickLessonIcon variant="outlined" />;
    if (category.name == "Deep Dives (2+ hours)") return <DeepDiveIcon />;
    if (category.name == "Win at Work") return <CupIcon variant="outlined" />;
    return <SupportIcon />;
  };

  const handleCloseFilter = () => {
    if (isFilterOpen) {
      setIsFilterOpen(false);
    }
    if (isFilterSectionOpen) {
      setIsFilterSectionOpen(false);
    }
  };

  return (
    <div className={styles.root}>
      {isBannerOpen && (
        <AcademyBannerCard onClose={() => setIsBannerOpen(false)} />
      )}
      <CategoryTitle
        title="Topics"
        onClick={() => setIsFilterSectionOpen(true)}
      />
      <div className={styles.categoriesContainer}>
        {isLoadingCategories
          ? new Array(4)
              .fill(1)
              .map((each, idx) => (
                <Skeleton
                  key={idx}
                  variant="rectangular"
                  className={styles.categoryLoader}
                />
              ))
          : categories.map((eachCategory) => {
              // if (!visibleCategories.find((each) => each === eachCategory.id))
              //   return null;
              return (
                <Tag
                  icon={getCategoryIcon(eachCategory)}
                  key={eachCategory.id}
                  label={eachCategory.name}
                  className={cx(styles.tag, styles.tagMotion)}
                  color={"info"}
                  size="small"
                  variant="outlined"
                  onClick={() => setSelectedCategory(eachCategory.id)}
                  classes={{ icon: styles.tagIcon }}
                />
              );
            })}
      </div>
      {shortCourses.length > 0 && (
        <>
          <CategoryTitle
            title="Quick Lessons (<1 hour)"
            onClick={() => setSelectedCategory(Number(CATEGORY_IDS.Short))}
          />
          <div className={styles.courseContainer}>
            {shortCourses.map((each) => (
              <MobileCourseCard key={each.id} course={each} />
            ))}
          </div>
        </>
      )}

      {deepCourses.length > 0 && (
        <>
          <CategoryTitle
            title="Deep Dives (2+ hours)"
            onClick={() => setSelectedCategory(Number(CATEGORY_IDS.Deep))}
          />
          <div className={styles.courseContainer}>
            {deepCourses.map((each) => (
              <MobileCourseCard key={each.id} course={each} />
            ))}
          </div>
        </>
      )}

      {languageCourses.length > 0 && (
        <>
          <CategoryTitle
            title="Learn a new language"
            onClick={() => setSelectedCategory(Number(CATEGORY_IDS.Language))}
          />
          <div className={styles.languageCourseContainer}>
            {languageCourses.map((each) => (
              <MobileLanguageCourseCard key={each.id} course={each} />
            ))}
          </div>
        </>
      )}
      {skillCourses.length > 0 && (
        <>
          <CategoryTitle
            title="Win at Work"
            onClick={() => setSelectedCategory(Number(CATEGORY_IDS.Win))}
          />
          <div className={styles.courseContainer}>
            {skillCourses.map((each) => (
              <MobileCourseCard course={each} key={each.id} />
            ))}
          </div>
        </>
      )}
      {selectedCategory && (
        <CategoryCoursesModal
          categoryId={selectedCategory}
          onClose={() => setSelectedCategory(undefined)}
        />
      )}
      {(isFilterOpen || isFilterSectionOpen) && (
        <MobileFilterSection onClose={handleCloseFilter} />
      )}
    </div>
  );
};

export default MobileAcademyMainPage;
